import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import classNames from 'classnames';
import { Row, Col } from 'reactstrap';
import { NSButton, NSLabel } from 'bricks';
import NSInputNumberStepper from 'bricks/NSInputNumberStepper/NSInputNumberStepper';
import './OperatingInformation.scss';
import { IOperatingAndPermanentDebtInfo, IPatchOperatingInfoData } from 'views/ProFormaTable/types';
import RenderIf from 'components/RenderIf/RenderIf';
import { ProFormaModelIds, IncomeCalculationType } from 'constants/proForma';
import { NSButtonBase } from 'bricks/NSButton/NSButton';
import {
    LEASE_UP_PACE_TOOLTIP,
    OCCUPANCY_UPON_STABILIZATION, PRO_FORMA_TABLE_NAV, PRO_FORMA_TABS, ProjectActualsDiscrepanciesEnum,
} from 'views/ProFormaTable/constants';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import MonthlyAnalysisTable, { IMonthlyAnalysisPeriod } from './MonthlyAnalysisTable/MonthlyAnalysisTable';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import { getMonthlyAnalysisPeriods, getStabilizedMonthMissingEntity } from './helpers';
import MetricOutputCard from '../../../MetricOutputCard/MetricOutputCard';

export interface IOperatingInformationProps {
    signUpMode?: boolean;
    navigateToSection: (sideNavName: string, tabName?: string) => void;
}

const OperatingInformation = ({ signUpMode = false, navigateToSection }: IOperatingInformationProps) => {
    const {
        proForma, operatingInfo, operatingPeriodCashFlows, handleUpdateProFormaOperations,
        cashFlowPeriods, shouldUseProjectActuals, projectActuals,
    } = useProFormaSocketContext();
    const isIncomeCalculationTypeEstimate = operatingInfo?.incomeCalculationType === IncomeCalculationType.ESTIMATE;
    const isApartment = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proForma.proFormaModelId);

    const [operatingInfoData, setOperatingInfoData] = useState<Partial<IOperatingAndPermanentDebtInfo>>({});

    const labelClassName = 'pl-0 col-4 font-weight-normal text-dark align-self-center';
    const labelClassNameNoWrap = 'pl-0 col-4 my-auto font-weight-normal text-dark text-nowrap';

    const actualsDiscrepancies = projectActuals?.projectActuals?.discrepancies || [];

    const updateOperationsKeys = (keyValues: Partial<IPatchOperatingInfoData>) => {
        if (operatingInfo?.id) {
            handleUpdateProFormaOperations(operatingInfo.id, keyValues);
        }
    };

    const monthlyAnalysisPeriods: IMonthlyAnalysisPeriod[] = getMonthlyAnalysisPeriods(
        proForma, cashFlowPeriods, operatingInfo, operatingPeriodCashFlows,
    );

    const redirectToDevelopmentTab = () => {
        navigateToSection(PRO_FORMA_TABLE_NAV.PRO_FORMA.name, PRO_FORMA_TABS.DEVELOPMENT.name);
    };

    const stabilizedMonthMissingEntity = getStabilizedMonthMissingEntity(proForma, isIncomeCalculationTypeEstimate);

    useEffect(() => {
        if (operatingInfo) {
            setOperatingInfoData({
                occupancyUponStabilizationPercentage: operatingInfo.occupancyUponStabilizationPercentage,
                rentalGrowthPercentage: operatingInfo.rentalGrowthPercentage,
                percentPreLeased: operatingInfo.percentPreLeased,
                leaseUpPace: operatingInfo.effectiveLeaseUpPace,
                userPaceReachesStabilization: operatingInfo.userPaceReachesStabilization,
                lengthOfLease: operatingInfo.lengthOfLease,
                initialFreeRent: operatingInfo.initialFreeRent,
                stabilizedFreeRent: operatingInfo.stabilizedFreeRent,
            });
        }
    }, [operatingInfo, operatingPeriodCashFlows]);

    return (
        <Row className="mx-n1 px-3">
            <Col className="px-1 my-3" sm={12} md={6}>
                <div className="d-flex align-items-center">
                    <div className={`d-flex my-auto ${labelClassName}`}>
                        <NSLabel className="my-auto font-weight-normal text-dark mr-1">Occupancy upon stabilization</NSLabel>
                        <IconWithTooltip
                            name="occupancyUponStabilization"
                            icon={faInfoCircle}
                            tooltipText={OCCUPANCY_UPON_STABILIZATION}
                        />
                    </div>
                    <NSInputNumberStepper
                        percentage
                        name="occupancyUponStabilizationPercentage"
                        value={operatingInfoData.occupancyUponStabilizationPercentage || null}
                        onChange={(value: number) => updateOperationsKeys({
                            occupancyUponStabilizationPercentage: value,
                        })}
                        min={0}
                        step={1}
                        max={100}
                        inputGroupClassName="OperatingInformation__input-width"
                    />
                </div>
                <RenderIf isTrue={!isApartment}>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={`my-auto ${labelClassName}`}>Income calculation</NSLabel>
                        <div className="my-auto font-weight-normal text-dark text-nowrap">
                            {
                                signUpMode ? (
                                    <>
                                        <NSButtonBase
                                            color={isIncomeCalculationTypeEstimate ? 'primary' : 'secondary'}
                                            outline={false}
                                            callback={() => updateOperationsKeys({ incomeCalculationType: IncomeCalculationType.ESTIMATE })}
                                            text="Allocation"
                                            className={classNames('px-3', { 'border border-primary bg-primary-lighten': false })}
                                        />
                                        <NSButtonBase
                                            color={isIncomeCalculationTypeEstimate ? 'secondary' : 'primary'}
                                            outline={false}
                                            callback={() => updateOperationsKeys({ incomeCalculationType: IncomeCalculationType.TENANTS })}
                                            text="Rent roll"
                                            className={classNames('px-3', {
                                                'border border-primary bg-primary-lighten': false,
                                            })}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <NSButton
                                            color={isIncomeCalculationTypeEstimate ? 'primary' : 'secondary'}
                                            outline={false}
                                            callback={() => updateOperationsKeys({ incomeCalculationType: IncomeCalculationType.ESTIMATE })}
                                            text="Allocation"
                                            className={classNames('px-3', { 'border border-primary bg-primary-lighten': false })}
                                        />
                                        <NSButton
                                            color={!isIncomeCalculationTypeEstimate ? 'primary' : 'secondary'}
                                            outline={false}
                                            callback={() => updateOperationsKeys({ incomeCalculationType: IncomeCalculationType.TENANTS })}
                                            text="Rent roll"
                                            className={classNames('px-3', {
                                                'border border-primary bg-primary-lighten': false,
                                            })}
                                        />
                                    </>
                                )
                            }
                        </div>
                    </div>
                </RenderIf>
                <RenderIf
                    isTrue={isIncomeCalculationTypeEstimate || isApartment}
                >
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Rent growth (annual)</NSLabel>
                        <NSInputNumberStepper
                            percentage
                            name="rentalGrowthPercentage"
                            value={operatingInfoData.rentalGrowthPercentage || null}
                            onChange={(value: number) => updateOperationsKeys({ rentalGrowthPercentage: value })}
                            min={0}
                            step={0.1}
                            max={100}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>% pre-leased</NSLabel>
                        <NSInputNumberStepper
                            percentage
                            name="percentPreLeased"
                            value={operatingInfoData.percentPreLeased || null}
                            onChange={(value: number) => updateOperationsKeys({ percentPreLeased: value })}
                            min={0}
                            step={1}
                            max={100}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <div className={`d-flex my-auto ${labelClassName}`}>
                            <NSLabel className="my-auto font-weight-normal text-dark mr-1">
                                Lease-up pace
                                {` (${isApartment ? 'units' : 'SF'}/month)`}
                            </NSLabel>
                            <RenderIf
                                isTrue={!operatingInfoData.userPaceReachesStabilization}
                            >
                                <IconWithTooltip
                                    name="occupancyUponStabilization"
                                    icon={faInfoCircle}
                                    tooltipText={LEASE_UP_PACE_TOOLTIP}
                                />
                            </RenderIf>
                        </div>
                        <NSInputNumberStepper
                            name="leaseUpPace"
                            value={operatingInfoData.leaseUpPace || null}
                            onChange={(value: number) => updateOperationsKeys({ leaseUpPace: value })}
                            min={0}
                            step={1}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Length of lease</NSLabel>
                        <NSInputNumberStepper
                            name="lengthOfLease"
                            value={operatingInfoData.lengthOfLease || null}
                            onChange={(value: number) => updateOperationsKeys({ lengthOfLease: value })}
                            min={1}
                            step={1}
                            customSuffix={(operatingInfo.lengthOfLease || 0) > 1 ? 'months' : 'month'}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>
                </RenderIf>
                <RenderIf isTrue={isApartment}>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Initial free rent</NSLabel>
                        <NSInputNumberStepper
                            name="initialFreeRent"
                            value={operatingInfoData.initialFreeRent || null}
                            onChange={(value: number) => updateOperationsKeys({ initialFreeRent: value })}
                            min={0}
                            step={1}
                            customSuffix={(operatingInfo.initialFreeRent || 0) > 1 ? 'months' : 'month'}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassName}>Stabilized free rent</NSLabel>
                        <NSInputNumberStepper
                            name="stabilizedFreeRent"
                            value={operatingInfoData.stabilizedFreeRent || null}
                            onChange={(value: number) => updateOperationsKeys({ stabilizedFreeRent: value })}
                            min={0}
                            step={1}
                            customSuffix={(operatingInfoData.stabilizedFreeRent || 0) > 1 ? 'months' : 'month'}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>
                </RenderIf>
            </Col>
            <Col className="px-1 my-3" sm={12} md={6}>
                <div>
                    <MetricOutputCard
                        label="Operation start"
                        value={operatingInfo?.operationStartDate && operatingInfo?.operationBeginMonth
                            ? `${format(parseISO(operatingInfo.operationStartDate), 'MMM yyyy')} (Month ${operatingInfo.operationBeginMonth})` : ''}
                        showInProgress={!operatingInfo.operationStartDate}
                        showProjectActualsIcon={shouldUseProjectActuals
                            && actualsDiscrepancies?.includes(ProjectActualsDiscrepanciesEnum.OPERATION_START_DATE)}
                        projectActualsIconName={ProjectActualsDiscrepanciesEnum.OPERATION_START_DATE}
                        inProgressText={(
                            <span>
                                In progress - provide start/end dates on
                                {' '}
                                <span
                                    role="presentation"
                                    onClick={redirectToDevelopmentTab}
                                    className="text-primary cursor--pointer"
                                >
                                    Development: Uses
                                </span>
                                .
                            </span>
                        )}
                    />
                </div>
                <div className="pt-2">
                    <MetricOutputCard
                        label="1st stabilized month"
                        value={operatingInfo.stabilizedDate
                            ? `${format(parseISO(operatingInfo.stabilizedDate), 'MMM yyyy')} (Month ${operatingInfo.stabilizedMonth})` : ''}
                        showInProgress={!operatingInfo.stabilizedDate}
                        inProgressText={(
                            <span>
                                In progress - provide operating information and
                                {' '}
                                {stabilizedMonthMissingEntity}
                                .
                            </span>
                        )}
                    />
                </div>
                <MonthlyAnalysisTable
                    proFormaModelId={proForma.proFormaModelId}
                    monthlyAnalysisPeriods={monthlyAnalysisPeriods}
                    operationBeginMonth={operatingInfo.operationBeginMonth}
                />
            </Col>
        </Row>
    );
};

export default OperatingInformation;
